// « Envoyer les accès à N » (D-103) : un geste pour toutes les fiches sans compte, après l'import FBI
// et depuis Membres. Un mineur : l'e-mail de ses parents ; un adulte : le sien. Avant : l'aperçu de
// l'e-mail et les comptes (« N e-mails, M déjà reliés, P sans e-mail »), puis la confirmation ;
// pendant : la progression ; après : le résultat, personne par personne. Chaque envoi passe par la
// fonction serveur `invite` (D-016), dont le bouton ouvre l'app du club sur son sous-domaine (D-104).
// Sans e-mail : le lien de l'équipe, à partager dans son groupe (un lien personnel, jamais).
import './reach.css';
import { invite, teamInviteLink } from '../lib/api';
import { clubAccessList } from '../lib/reach-api';
import { linksFor } from '../lib/club-links';
import {
  accessLabel, accessPlan, byAddress, countsLine, mailPreview, nameOf, sendOutcome, teamLinkText, toFamilies, type AccessPlan, type AccessRow, type Planned,
} from '../domain/access';
import type { ClubLinks } from '../domain/links';
import type { Ctx } from './context';
import { $, $$, armed, html, mount, openSheet, toast, type Html } from './dom';
import { openShare } from './share';

/** « Inès R., Jules F. et 3 autres ». */
export const someNames = (list: string[], max = 5) => list.length <= max ? list.join(', ')
  : `${list.slice(0, max).join(', ')} et ${list.length - max} ${list.length - max > 1 ? 'autres' : 'autre'}`;
const TONE = { ok: 'chip-ok', warn: 'chip-warn', mute: 'chip-mute', ko: 'chip-ko' } as const;
const today = () => new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

/** La carte de Membres : « Envoyer les accès à N », et les comptes. */
export function accessCard(rows: AccessRow[]): Html {
  if (!rows.length) return html``;
  const p = accessPlan(rows);
  const n = p.send.length;
  const title = n ? `Envoyer les accès à ${n}` : p.noEmail.length ? `${p.noEmail.length} sans e-mail · le lien de l’équipe` : 'Les accès du club';
  return html`<button type="button" class="card card-row sa-card ${n ? 'sa-card-go' : ''}" id="mm-access" data-guide="mm-access">
    <span class="play-thumb" aria-hidden="true"><span class="ti ti-megaphone"></span></span>
    <span class="card-main"><span class="card-title">${title}</span><span class="muted small">${countsLine(p)}${p.check.length ? ` · ${p.check.length} à vérifier` : ''}</span></span>
    <span class="occ-go" aria-hidden="true">›</span></button>`;
}

/** Les fiches sans e-mail, par équipe (la première de chaque personne). */
function noEmailTeams(ctx: Ctx, rows: AccessRow[]) {
  const by = new Map<string, { id: string; name: string; rows: AccessRow[] }>();
  for (const r of rows) {
    const id = r.team_ids[0];
    if (!id) continue;
    const name = ctx.full.teams.find(t => t.id === id)?.name ?? r.teams?.split(', ')[0] ?? 'Équipe';
    if (!by.has(id)) by.set(id, { id, name, rows: [] });
    by.get(id)!.rows.push(r);
  }
  return [...by.values()].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
}

function noEmailBlock(ctx: Ctx, rows: AccessRow[]): Html {
  if (!rows.length) return html``;
  const teams = noEmailTeams(ctx, rows);
  return html`<section class="sa-block" id="sa-noemail">
    <h4 class="sa-h">Sans e-mail · ${rows.length}</h4>
    <p class="muted small">Pas d’adresse dans l’import : partage le lien de l’équipe dans son groupe WhatsApp. Chacun se présente, le coach valide et retrouve sa fiche. Un lien personnel, lui, ne se poste jamais dans un groupe.</p>
    <ul class="iv-list">${teams.map(t => html`<li class="iv-row"><span class="iv-main"><strong>${t.name} · ${t.rows.length}</strong>
      <span class="muted small">${someNames(t.rows.map(nameOf))}</span></span>
      <button type="button" class="btn btn-ghost btn-sm" data-tlink="${t.id}">Lien de l’équipe</button></li>`)}</ul></section>`;
}

function wireNoEmail(sheet: HTMLElement, ctx: Ctx, rows: AccessRow[], links: ClubLinks): void {
  const club = ctx.club?.name ?? 'club';
  const teams = noEmailTeams(ctx, rows);
  for (const b of $$<HTMLButtonElement>(sheet, '[data-tlink]')) b.addEventListener('click', async () => {
    const t = teams.find(x => x.id === b.dataset.tlink);
    if (!t) return;
    try {
      const l = await teamInviteLink(t.id);
      openShare(`Lien de l’équipe · ${t.name}`,
        'À poster dans le groupe de l’équipe : chacun se présente, le coach valide. C’est le seul lien qui se partage dans un groupe.',
        teamLinkText({ club, team: t.name, url: links.invite(l.token), families: toFamilies(t.rows) }));
    } catch { toast('Lien impossible pour le moment.', 'ko'); }
  });
}

function tracking(rows: AccessRow[]): Html {
  // Envoyés (en attente) d'abord, puis reliés, puis à envoyer, puis sans e-mail.
  const order = { warn: 0, ok: 1, mute: 2, ko: 3 } as const;
  const rank = (r: AccessRow) => order[accessLabel(r).tone];
  const sorted = [...rows].sort((a, b) => rank(a) - rank(b) || a.first_name.localeCompare(b.first_name, 'fr'));
  return html`<details class="sa-track"><summary>Suivi des accès · ${rows.length} ${rows.length > 1 ? 'licenciés' : 'licencié'}</summary>
    <ul class="sa-list">${sorted.map(r => { const l = accessLabel(r); return html`<li class="sa-trow">
      <span class="sa-main"><strong>${nameOf(r)}</strong><small>${r.teams ?? ''}</small></span><span class="chip ${TONE[l.tone]}">${l.text}</span></li>`; })}</ul></details>`;
}

/** La fiche « Envoyer les accès » : aperçu et confirmation, progression, résultat. */
export async function openSendAccess(ctx: Ctx, onDone?: () => void, preloaded?: AccessRow[]): Promise<void> {
  const club = ctx.club;
  if (!club) return;
  let rows: AccessRow[], links: ClubLinks;
  try { [rows, links] = await Promise.all([preloaded ? Promise.resolve(preloaded) : clubAccessList(club.id), linksFor(club.id)]); }
  catch { toast('Liste des accès indisponible pour le moment.', 'ko'); return; }
  const plan: AccessPlan = accessPlan(rows);
  const pool: Planned[] = [...plan.send, ...plan.check];
  const picked = new Set(plan.send.map(p => p.row.person_id));
  let tab: 'parent' | 'player' = plan.send.some(p => p.kind === 'parent') || !plan.send.length ? 'parent' : 'player';
  let sent = false, sheetOpen = true;

  openSheet('Envoyer les accès', html`<div class="sa" id="sa"></div>`, (sheet, close) => {
    const root = $(sheet, '#sa')!;
    const chosen = () => pool.filter(p => picked.has(p.row.person_id));
    const goLabel = () => { const n = chosen().length; return n ? `Envoyer ${n > 1 ? `les ${n} e-mails` : 'l’e-mail'}` : 'Choisis au moins une personne'; };
    const pick = (p: Planned): Html => html`<li><label class="sa-pick">
      <input type="checkbox" data-pick="${p.row.person_id}" ${picked.has(p.row.person_id) ? 'checked' : ''}>
      <span class="sa-main"><strong>${nameOf(p.row)}</strong>
        <small>${p.row.teams ?? ''} · ${p.kind === 'parent' ? 'e-mail des parents' : 'son e-mail'} : ${p.email}</small>
        ${p.check === 'sheet' ? html`<small class="sa-warn">Pas d’e-mail de parent : celui de sa fiche est-il celui de la famille ?</small>`
          : p.check === 'twice' ? html`<small class="sa-warn">Même adresse qu’un autre adulte : une adresse ne sert qu’à une fiche.</small>` : ''}</span></label></li>`;

    const drawMail = () => {
      const m = mailPreview(club.name, tab);
      const n = chosen().filter(p => p.kind === tab).length;
      mount($(root, '#sa-mail')!, html`
        <div class="sa-mail-top"><span class="sa-mail-from">Arceau</span><span class="muted small">à ${n} ${tab === 'parent' ? (n > 1 ? 'parents' : 'parent') : (n > 1 ? 'adultes' : 'adulte')}</span></div>
        <strong class="sa-mail-subj">${m.subject}</strong>
        <p>${m.lines[0]}</p><p>${m.lines[1]}</p>
        <span class="sa-mail-btn">${m.button}</span>
        <p class="sa-mail-small">${m.lines[2]}</p>
        <p class="sa-mail-link">Le bouton ouvre ${links.app}</p>`);
    };
    const refresh = () => {
      const c = chosen();
      $(root, '#sa-counts')!.textContent = countsLine({ send: c, linked: plan.linked, noEmail: plan.noEmail });
      const go = $<HTMLButtonElement>(root, '#sa-go');
      if (go) { go.textContent = goLabel(); go.disabled = !c.length; delete go.dataset.armed; go.classList.remove('armed'); }
      drawMail();
    };

    const drawPreview = () => {
      const both = pool.some(p => p.kind === 'parent') && pool.some(p => p.kind === 'player');
      mount(root, html`
        <p class="sa-counts" id="sa-counts" aria-live="polite"></p>
        ${pool.length ? html`
          ${both ? html`<div class="seg" role="tablist" aria-label="Aperçu de l’e-mail">
            <button type="button" role="tab" data-tab="parent" aria-selected="${String(tab === 'parent')}">Aux parents</button>
            <button type="button" role="tab" data-tab="player" aria-selected="${String(tab === 'player')}">Aux adultes</button></div>` : ''}
          <figure class="sa-mail" id="sa-mail" aria-label="Aperçu de l’e-mail"></figure>
          <details class="sa-who" ${plan.send.length <= 6 ? 'open' : ''}><summary>${plan.send.length > 1 ? `Les ${plan.send.length} destinataires` : 'Le destinataire'}</summary>
            <ul class="sa-list">${plan.send.map(pick)}</ul></details>
          ${plan.check.length ? html`<section class="sa-block"><h4 class="sa-h">À vérifier · ${plan.check.length}</h4>
            <p class="muted small">Décochés : coche ceux que tu veux inviter quand même.</p>
            <ul class="sa-list">${plan.check.map(pick)}</ul></section>` : ''}
          <p class="hint">Un parent qui a déjà un compte est relié sans e-mail : il retrouve son enfant à sa prochaine visite. Deux enfants, une adresse : un seul compte.</p>
          <button type="button" class="btn btn-primary sa-go" id="sa-go" data-guide="sa-go"></button>`
          : html`<p class="muted">${plan.noEmail.length ? 'Personne à inviter par e-mail : il reste le lien de l’équipe pour ceux qui n’ont pas d’adresse.' : 'Tout le monde a déjà son accès.'}</p>`}
        ${noEmailBlock(ctx, plan.noEmail)}
        ${tracking(rows)}`);
      if (pool.length) refresh(); else $(root, '#sa-counts')!.textContent = countsLine(plan);
      for (const b of $$<HTMLButtonElement>(root, '[data-tab]')) b.addEventListener('click', () => {
        tab = b.dataset.tab as 'parent' | 'player';
        $$(root, '[data-tab]').forEach(x => x.setAttribute('aria-selected', String(x === b)));
        drawMail();
      });
      for (const c of $$<HTMLInputElement>(root, '[data-pick]')) c.addEventListener('change', () => {
        if (c.checked) picked.add(c.dataset.pick!); else picked.delete(c.dataset.pick!);
        refresh();
      });
      $(root, '#sa-go')?.addEventListener('click', e => {
        const b = e.currentTarget as HTMLButtonElement;
        const n = chosen().length;
        if (!n || !armed(b, `Confirmer : ${n > 1 ? `${n} e-mails partent` : 'l’e-mail part'}`)) return;
        void run(chosen());
      });
      wireNoEmail(root, ctx, plan.noEmail, links);
    };

    const run = async (list: Planned[]) => {
      sent = true;
      const total = list.length;
      const done: { p: Planned; ok: boolean; text: string; status?: 'invited' | 'linked' }[] = [];
      mount(root, html`<div class="sa-run" aria-live="polite">
        <p class="sa-run-t"><strong id="sa-n">0</strong> sur ${total} · envoi en cours</p>
        <div class="sa-bar" aria-hidden="true"><span id="sa-bar"></span></div>
        <ul class="sa-log" id="sa-log"></ul>
        <p class="hint">Garde cette fiche ouverte jusqu’à la fin : quelques secondes par e-mail.</p></div>`);
      const log = (d: (typeof done)[number]) => {
        done.push(d);
        const n = $(root, '#sa-n'), bar = $(root, '#sa-bar'), ul = $(root, '#sa-log');
        if (n) n.textContent = String(done.length);
        if (bar) bar.style.width = `${Math.round(100 * done.length / total)}%`;
        ul?.insertAdjacentHTML('afterbegin', html`<li class="${d.ok ? '' : 'ko'}">${d.ok ? '✓' : '✗'} <strong>${nameOf(d.p.row)}</strong> · ${d.text}</li>`.s);
      };
      // Trois envois à la fois ; ceux d'une même adresse l'un après l'autre (un seul compte créé).
      const groups = byAddress(list);
      const worker = async () => {
        for (let g = groups.shift(); g; g = groups.shift()) {
          for (const p of g) {
            const r = await invite(p.kind, p.row.person_id, p.email, undefined, undefined, undefined, links.app).catch(() => ({ error: 'network' }) as { status?: 'invited' | 'linked'; error?: string });
            log({ p, ...sendOutcome(r), status: r.status });
          }
        }
      };
      await Promise.all([worker(), worker(), worker()]);
      const invited = done.filter(d => d.status === 'invited').length, linked = done.filter(d => d.status === 'linked').length;
      const failed = done.filter(d => !d.ok);
      if (!sheetOpen) { toast(`${invited} ${invited > 1 ? 'e-mails envoyés' : 'e-mail envoyé'}${failed.length ? ` · ${failed.length} à reprendre` : ''}`, failed.length ? 'ko' : 'ok'); return; }
      const order = (d: (typeof done)[number]) => (d.ok ? 1 : 0);
      mount(root, html`
        <section class="sa-done"><p class="eyebrow">C’est parti</p>
          <h3>${invited} ${invited > 1 ? 'e-mails envoyés' : 'e-mail envoyé'}</h3>
          <p class="muted">${[linked ? `${linked} ${linked > 1 ? 'reliés à leur compte' : 'relié à son compte'} (déjà dans l’app, sans e-mail)` : '', failed.length ? `${failed.length} à reprendre` : '']
            .filter(Boolean).join(' · ') || 'Chacun reçoit son e-mail, puis un code pour se connecter.'}</p></section>
        <ul class="sa-list">${[...done].sort((a, b) => order(a) - order(b) || a.p.row.first_name.localeCompare(b.p.row.first_name, 'fr')).map(d => html`<li class="sa-trow">
          <span class="sa-main"><strong>${nameOf(d.p.row)}</strong><small>${d.p.email}</small></span>
          <span class="chip ${d.ok ? (d.status === 'invited' ? 'chip-warn' : 'chip-ok') : 'chip-ko'}">${d.status === 'invited' ? `envoyé le ${today()}` : d.status === 'linked' ? 'relié' : d.text}</span></li>`)}</ul>
        ${failed.length ? html`<p class="hint">À reprendre depuis sa fiche (Membres) : corrige l’adresse, ou envoie son lien en message privé.</p>` : ''}
        <p class="hint">« Envoyé » devient « relié » dès que la personne ouvre l’app avec le code reçu.</p>
        ${noEmailBlock(ctx, plan.noEmail)}
        <button type="button" class="btn btn-primary" id="sa-end">Terminé</button>`);
      wireNoEmail(root, ctx, plan.noEmail, links);
      $(root, '#sa-end')!.addEventListener('click', () => close());
    };

    drawPreview();
  }, () => { sheetOpen = false; if (sent) onDone?.(); });
}
